import { useEffect, useState } from 'react';
import type { Idea, IdeaSession } from '../api';
import * as api from '../api';

// Sessions in the idea panel (P5-12): what each Claude session did on the idea,
// newest first — written by log_session (Claude Code, claude.ai) or a Claude
// build's report step. The newest is open; older ones fold. Hidden when none.

const SOURCE: Record<IdeaSession['kind'], { icon: string; label: string }> = {
  action: { icon: '⬡', label: 'GitHub build' },
  local: { icon: '◈', label: 'Claude Code' },
  web: { icon: '◎', label: 'claude.ai' },
};

function parseSql(s: string): Date {
  return new Date(s.replace(' ', 'T') + 'Z');
}

function when(s: IdeaSession): string {
  const end = parseSql(s.ended_at ?? s.created_at);
  const date = end.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  const ms = s.duration_ms ?? (s.started_at && s.ended_at ? end.getTime() - parseSql(s.started_at).getTime() : null);
  if (ms === null || ms < 60_000) return date;
  const mins = Math.round(ms / 60_000);
  return `${date} · ${mins >= 90 ? `${Math.round(mins / 60)} h` : `${mins} min`}`;
}

function List({ label, items }: { label: string; items: string[] }) {
  if (items.length === 0) return null;
  return (
    <div className="session-list">
      <div className="session-list-label mono">{label}</div>
      <ul>{items.map((t, i) => <li key={i}>{t}</li>)}</ul>
    </div>
  );
}

function ResumeButton({ id }: { id: string }) {
  const [copied, setCopied] = useState(false);
  const cmd = `claude --resume ${id}`;
  return (
    <button
      className="btn btn-secondary btn-sm"
      title={cmd}
      onClick={() => {
        void navigator.clipboard?.writeText(cmd).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        });
      }}
    >
      {copied ? 'Copied' : 'Copy resume command'}
    </button>
  );
}

function SessionCard({ s, open }: { s: IdeaSession; open: boolean }) {
  const src = SOURCE[s.kind];
  const failed = s.conclusion === 'failure';
  const t = s.tests;
  return (
    <details className="session-card" open={open}>
      <summary>
        <span className="session-src mono" aria-hidden="true">{src.icon}</span>
        <span className="session-head">
          <span className="session-meta">
            {src.label} · {when(s)}
            {s.conclusion && <span className={failed ? 'session-failed' : 'session-ok'}> · {failed ? '✗' : '✓'} {s.conclusion}</span>}
          </span>
          <span className="session-summary">{s.summary}</span>
        </span>
      </summary>
      <div className="session-body">
        {s.next_step && (
          <p className="session-next"><span className="mono">Next</span> {s.next_step}</p>
        )}
        {t && (
          <p className="session-tests">
            Tests: {t.passed}/{t.ran} passed{t.failed > 0 ? `, ${t.failed} failed` : ''}{t.notes ? ` — ${t.notes}` : ''}
          </p>
        )}
        <List label="Open questions" items={s.open_questions} />
        <List label="Follow-ups" items={s.follow_ups} />
        <List label="Decisions" items={s.decisions} />
        <List label="Changes" items={s.changes} />
        {s.branch && <p className="session-branch mono">⎇ {s.branch}</p>}
        {(s.pr_url || s.run_url || s.session_url || s.claude_session_id) && (
          <div className="detail-actions-inline">
            {s.pr_url && <a className="btn btn-secondary btn-sm" href={s.pr_url} target="_blank" rel="noopener noreferrer">Pull request</a>}
            {s.run_url && <a className="btn btn-secondary btn-sm" href={s.run_url} target="_blank" rel="noopener noreferrer">Run log</a>}
            {s.session_url && <a className="btn btn-secondary btn-sm" href={s.session_url} target="_blank" rel="noopener noreferrer">Open chat</a>}
            {s.claude_session_id && s.kind !== 'web' && <ResumeButton id={s.claude_session_id} />}
          </div>
        )}
      </div>
    </details>
  );
}

export function SessionsSection({ idea }: { idea: Idea }) {
  const [sessions, setSessions] = useState<IdeaSession[]>([]);

  useEffect(() => {
    let live = true;
    setSessions([]);
    api.listIdeaSessions(idea.id)
      .then((r) => { if (live) setSessions(r.sessions); })
      .catch(() => { /* the panel works without it */ });
    return () => { live = false; };
  }, [idea.id]);

  if (sessions.length === 0) return null;

  return (
    <div className="detail-section">
      <div className="detail-label">SESSIONS</div>
      <div className="session-cards">
        {sessions.map((s, i) => <SessionCard key={s.id} s={s} open={i === 0} />)}
      </div>
    </div>
  );
}
